/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team
 * <mailto:radmanesh@gmail.com Arman Radmanesh>
 * <mailto:salarirad@gmail.com Mostafa Salari Rad>
 */
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import ProjectCard from '../components/ProjectCard';
import './admin.css';

/**
 * Represents the admin dashboard page.
 * @returns {JSX.Element} The admin dashboard component.
 */
const AdminDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const navigate = useNavigate();

  // Get all projects from the database
  useEffect(() => {
    setIsLoading(true);
    const unsubscribe = onSnapshot(collection(db, 'projects'), (snapshot) => {
      setProjects(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      setIsLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleDeleteProject = (projectId) => {
    setProjectToDelete(projects.find(project => project.id === projectId));
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!projectToDelete) return;
    setIsLoading(true);
    try {
      // deleteDoc(doc(db, "projects", projectToDelete.id)) — kept commented per original
    } catch (error) {
      setAlert({ type: 'error', message: 'Project could not be deleted!' });
      console.error('Error deleting project: ', error);
    }
    setIsLoading(false);
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
    setTimeout(() => setAlert(null), 5000);
  };

  const handleProjectAction = ({ action, data }) => {
    console.debug('Action:', action, 'Data:', data);
    switch (action) {
      case 'participants':
        navigate(`/admin/participants/${data}`);
        break;
      case 'show':
        navigate(`/admin/showProject/${data}`);
        break;
      default:
        console.warn('Unknown action:', action);
    }
  };

  const initializeBigQueryTables = async () => {
    try {
      setIsLoading(true);
      // await initializeBigQuery(); — not yet implemented
      setIsLoading(false);
      alert('BigQuery tables created successfully!');
    } catch (error) {
      console.error('Error creating tables:', error);
      alert('Error creating tables. Please check the console.');
    }
  };

  const displayName = auth?.currentUser?.displayName;

  return (
    <div className="ja-root">
      <div className="ja-page">

        {/* Page header */}
        <div className="ja-page-header">
          <div>
            <span className="ja-label">admin</span>
            <h1 className="ja-page-title">
              <strong>Dashboard</strong>
            </h1>
            {displayName && (
              <p className="ja-page-subtitle">
                Welcome back, <strong>{displayName}</strong>
              </p>
            )}
          </div>
          <div className="ja-btn-row">
            <button className="ja-btn ja-btn-ghost" onClick={initializeBigQueryTables} disabled={isLoading}>
              Init BigQuery
            </button>
            <button className="ja-btn ja-btn-primary" onClick={() => navigate('/admin/newProject')}>
              + New project
            </button>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="ja-progress">
            <div className="ja-progress-bar" />
          </div>
        )}

        {/* Alert */}
        {alert && (
          <div className={`ja-alert ja-alert-${alert.type}`}>
            {alert.message}
          </div>
        )}

        {/* Projects list */}
        {!isLoading && projects.length === 0 ? (
          <div className="ja-empty">
            <div className="ja-empty-title">No projects yet</div>
            <div className="ja-empty-sub">Create your first project to start collecting wearable data.</div>
            <button className="ja-btn ja-btn-primary" onClick={() => navigate('/admin/newProject')}>
              + New project
            </button>
          </div>
        ) : (
          <div className="ja-grid">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                handleDeleteProject={handleDeleteProject}
                handleProjectAction={handleProjectAction}
              />
            ))}
          </div>
        )}

        {/* Delete dialog */}
        {deleteDialogOpen && (
          <div className="ja-dialog-backdrop" onClick={() => setDeleteDialogOpen(false)}>
            <div className="ja-dialog" onClick={e => e.stopPropagation()}>
              <div className="ja-dialog-title">Delete project?</div>
              <div className="ja-dialog-body">
                Are you sure you want to delete <strong>{projectToDelete?.name}</strong>?
                This action cannot be undone.
              </div>
              <div className="ja-dialog-actions">
                <button className="ja-btn ja-btn-ghost" onClick={() => setDeleteDialogOpen(false)}>
                  Cancel
                </button>
                <button className="ja-btn ja-btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
